import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Check, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
const ORANGE = '#ee7203';
const PURPLE = '#7a2182';
/**
 * Line workspace — the panel that slides out to the right of Create Quote and handles ONE product line at a time, so the quote itself can stay clean.
 *   Top:     a search for a product code or description (the same stock search the quote lines use).
 *   Section: CUSTOMER PRICING — quantity, unit of measure, price, price unit and the line total.
 *   Section: COSTING (under it) — everything that used to live in the line's drop-down: list price, cost factor, discount, H/O support, rebate, MF support,
 *            ad-hoc and margin, with the same green / yellow / red highlighting.
 * Header: the Add Product Group button sits in the middle of it. Above Costing is the Support Document (shown once for the whole quote).
 * Boxes: every entry box is the same size with rounded corners; clicking or tabbing into one selects what is in it so typing replaces it straight away, and Tab goes
 * box to box (the small buttons inside the boxes are skipped). The quantity starts at 0 and stays yellow until it is filled.
 * Keyboard: the search box has the cursor when the panel opens. Enter picks the product and moves on to the quantity, then the price, then starts the next line.
 * In the costing boxes Enter moves to the next box and, after the last one, starts the next line. "+" anywhere adds a new line (handled by the quote).
 *
 * Everything it changes goes through the quote's own functions (`ctx`), so a line behaves exactly as it does in the table.
 */
export default function QuoteLineEditor({ index, item, lineCount, ctx, onClose, onAddLine, onGoTo, focusSignal }) {
  const rootRef = useRef(null);
  const nextAfterPickRef = useRef('qty');
  const [query, setQuery] = useState(item?.code || '');
  const [qtyText, setQtyText] = useState(String(item?.quantity ?? 0));
  const [qtyTouched, setQtyTouched] = useState(false);
  const {
    ProductSearch, updateItem, updateMarginConfig, handleProductSelect, handlePriceUomChange, lookupCostFactorForCode, toggleHoSupportApply,
    mfSupportDocs, openProductGroup, globalSupportDocId, applyGlobalSupportDocument, getMarginState, calcBoxClass, getLineTotal, hasValidCostFactor, getItemCostFactor, getQtyEach,
    calculateMarginCost, formatCurrency, fmtPriceDate, fmtLastBought, uomShort, UOM_OPTIONS,
  } = ctx;
  // A different line was opened: the search box shows its code, the quantity box its quantity, and nothing is "confirmed" yet.
  useEffect(() => { setQuery(item?.code || ''); setQtyTouched(false); }, [index]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (parseInt(qtyText, 10) !== item?.quantity) setQtyText(String(item?.quantity ?? 0));
  }, [index, item?.quantity]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const focusField = (name) => {
    const el = rootRef.current && rootRef.current.querySelector(`[data-line-field="${name}"]:not([disabled])`);
    if (el) { el.focus(); if (typeof el.select === 'function') el.select(); }
  };
  const focusSoon = (name) => setTimeout(() => focusField(name), 80);
  // The search found a product, or the person typed a code that is not in stock and pressed Enter.
  const pickProduct = (product) => {
    const code = String(product.product_code || product.code || '').trim();
    nextAfterPickRef.current = 'qty';
    setQuery(code);
    handleProductSelect(index, product);
  };
  const submitText = (text) => {
    const code = String(text || '').trim().toUpperCase();
    if (!code) return;
    nextAfterPickRef.current = item.description ? 'qty' : 'desc';
    setQuery(code);
    updateItem(index, 'code', code);
    lookupCostFactorForCode(index, code);
  };
  const afterPick = () => focusSoon(nextAfterPickRef.current);
  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || (e.nativeEvent && e.nativeEvent.isComposing)) return;
    const el = e.target;
    const name = el && el.getAttribute ? el.getAttribute('data-line-field') : null;
    if (!name) return;
    e.preventDefault();
    if (name === 'search') { if (item.code) focusField('qty'); return; } // (with results showing, the search box takes the Enter itself)
    if (name === 'desc') { focusField('qty'); return; }
    if (name === 'qty') { setQtyTouched(true); focusField('price'); return; }
    if (name === 'price') { onAddLine(); return; }
    if (name === 'cost') {
      const boxes = Array.from(rootRef.current.querySelectorAll('[data-line-field="cost"]:not([disabled])'));
      const at = boxes.indexOf(el);
      if (at >= 0 && at < boxes.length - 1) { boxes[at + 1].focus(); if (boxes[at + 1].select) boxes[at + 1].select(); } else onAddLine();
    }
  };
  // Clicking or tabbing into a box selects what is in it, so typing replaces it straight away; leaving it without typing leaves it exactly as it was. (The mouse
  // press that gave the box focus would otherwise drop the selection again, so that one mouse-up is swallowed.)
  const focusProps = {
    onMouseDown: (e) => { e.currentTarget.dataset.fresh = document.activeElement === e.currentTarget ? '' : '1'; },
    onFocus: (e) => { if (e.target && typeof e.target.select === 'function') e.target.select(); },
    onMouseUp: (e) => { if (e.currentTarget.dataset.fresh === '1') { e.preventDefault(); e.currentTarget.dataset.fresh = ''; } },
  };
  const cfg = item.margin_config || null;
  const hasCf = hasValidCostFactor(item);
  const qtyUnit = item.qty_unit || 'Each';
  const priceUnit = item.unit || 'Each';
  // Units that need a Cost Factor (M2, Length, M) are only offered once the product has one; until then it is sold Each.
  const unitChoices = (current) => {
    const base = hasCf ? UOM_OPTIONS : ['Each'];
    return base.includes(current) ? base : [...base, current];
  };
  const state = getMarginState(item);
  const priceNum = parseFloat(item.unit_price) || 0;
  const priceCls = state === 'red' ? 'border-red-500 bg-red-50' : state === 'yellow' ? 'border-yellow-400 bg-yellow-50' : (priceNum > 0 ? 'border-green-400 bg-green-50' : '');
  // Yellow until a quantity is entered, then green.
  const qtyFilled = (parseInt(qtyText, 10) || 0) > 0;
  const qtyCls = !qtyFilled ? 'border-yellow-400 bg-yellow-50' : (qtyTouched ? 'border-green-400 bg-green-50' : '');
  const costBasis = cfg ? calculateMarginCost({ ...cfg, margin_pct: 0 }) : 0;
  const marginGbp = costBasis > 0 ? priceNum - costBasis : null;
  const marginPct = costBasis > 0 && priceNum > 0 ? (marginGbp / priceNum) * 100 : null;
  const lastPriceNote = item.customer_price_date
    ? (item.customer_price_source === 'order' ? fmtLastBought(item.customer_price_value ?? item.unit_price, item.customer_price_date)
      : `${item.customer_price_source === 'manual' ? 'Price set' : 'Last priced'} ${fmtPriceDate(item.customer_price_date)}`)
    : '';
  const unitSelectCls = 'h-9 w-full rounded-xl border px-1 text-center text-sm bg-white';
  // Every entry box is this size with these corners.
  const boxCls = 'h-9 rounded-xl text-sm';
  const cost = (patch = {}) => ({ 'data-line-field': 'cost', ...focusProps, ...patch });
  return (
    <div ref={rootRef} className="h-full flex flex-col bg-white" onKeyDown={onKeyDown} data-testid="quote-line-editor">
      {/* Header: slide back, which line, previous / next, Done */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-orange-100 flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 hover:opacity-90"
          style={{ backgroundColor: ORANGE }}
          title="Slide this panel back"
          aria-label="Slide this panel back"
          data-testid="quote-line-editor-back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-none whitespace-nowrap">
          <h3 className="text-base font-extrabold leading-tight v3-qle-title">Product line</h3>
          <p className="text-[11px] text-gray-500" data-testid="quote-line-editor-position">Line {index + 1} of {lineCount}</p>
        </div>
        <Button type="button" onClick={openProductGroup} className="rounded-xl h-9 px-2.5 text-[13px] whitespace-nowrap text-white font-semibold flex-shrink-0" style={{ backgroundColor: ORANGE }} data-testid="add-product-group-btn">
          <Package className="w-4 h-4 mr-1.5" /> Add Product Group
        </Button>
        <div className="flex-1 min-w-0" aria-hidden="true" />
        <button type="button" onClick={() => onGoTo(index - 1)} disabled={index <= 0} className="w-7 h-7 rounded-full border flex items-center justify-center disabled:opacity-30" title="Previous line" data-testid="quote-line-editor-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onGoTo(index + 1)} disabled={index >= lineCount - 1} className="w-7 h-7 rounded-full border flex items-center justify-center disabled:opacity-30" title="Next line" data-testid="quote-line-editor-next">
          <ChevronRight className="w-4 h-4" />
        </button>
        <Button onClick={onClose} className="rounded-xl h-9 px-4 text-sm text-white font-semibold" style={{ backgroundColor: '#9333ea' }} data-testid="quote-line-editor-done">
          <Check className="w-4 h-4 mr-1" /> Done
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {/* Product search: a code or a description */}
        <div data-testid="quote-line-editor-search">
          <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Product — search a code or description</Label>
          <ProductSearch
            value={query}
            onChange={setQuery}
            onSelect={pickProduct}
            onPicked={afterPick}
            onSubmitText={submitText}
            keyboard
            matchWidth
            focusSignal={focusSignal}
            inputAttrs={{ 'data-line-field': 'search', 'data-testid': 'quote-line-editor-search-input' }}
            placeholder="Code or description…"
          />
          <Input
            value={item.description || ''}
            onChange={(e) => updateItem(index, 'description', e.target.value)}
            placeholder="Description"
            className={`mt-2 w-full ${boxCls}`}
            {...focusProps}
            data-line-field="desc"
            data-testid="quote-line-editor-description"
          />
        </div>
        {/* CUSTOMER PRICING */}
        <section className="rounded-2xl border-2 p-3" style={{ borderColor: ORANGE }} data-testid="quote-line-editor-pricing">
          <div className="flex items-baseline gap-2 mb-2">
            <h4 className="text-xs font-extrabold tracking-wider uppercase" style={{ color: ORANGE }}>Customer pricing</h4>
            <span className="text-[11px] text-gray-400">what the customer is quoted</span>
          </div>
          <div className="grid grid-cols-[1fr_72px_16px_1fr_72px] gap-2 items-end">
            <div>
              <Label className="text-[10px] text-gray-600">Quantity</Label>
              <Input
                type="number" min="0" value={qtyText}
                {...focusProps}
                onChange={(e) => {
                  setQtyText(e.target.value); setQtyTouched(true);
                  const n = parseInt(e.target.value, 10);
                  if (n >= 0) updateItem(index, 'quantity', n);
                }}
                onBlur={() => { if (qtyText.trim() === '' || isNaN(parseInt(qtyText, 10))) setQtyText(String(item.quantity ?? 0)); }}
                className={`text-center ${boxCls} ${qtyCls}`}
                data-line-field="qty"
                data-testid="quote-line-editor-qty"
              />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600">Per</Label>
              <select value={qtyUnit} onChange={(e) => updateItem(index, 'qty_unit', e.target.value)} className={unitSelectCls} data-testid="quote-line-editor-qty-uom">
                {unitChoices(qtyUnit).map((u) => <option key={u} value={u}>{uomShort(u)}</option>)}
              </select>
            </div>
            <div className="h-9 flex items-center justify-center text-gray-300 select-none" aria-hidden="true">|</div>
            <div>
              <Label className="text-[10px] text-gray-600">Price (£)</Label>
              <Input
                type="number" step="0.01" min="0" value={item.unit_price}
                {...focusProps}
                onChange={(e) => updateItem(index, 'unit_price', Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                className={`text-center ${boxCls} ${priceCls}`}
                data-line-field="price"
                data-testid="quote-line-editor-price"
              />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600">Per</Label>
              <select value={priceUnit} onChange={(e) => handlePriceUomChange(index, e.target.value)} className={unitSelectCls} data-testid="quote-line-editor-price-uom">
                {unitChoices(priceUnit).map((u) => <option key={u} value={u}>{uomShort(u)}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500 gap-2">
            <span className="min-w-0 truncate" data-testid="quote-line-editor-hint">
              {qtyUnit !== 'Each' ? `${qtyText || 0} ${uomShort(qtyUnit)} = ${getQtyEach(item)} Ea` : (!hasCf && item.code ? 'No cost factor on record: sold Each' : (lastPriceNote || ''))}
              {state === 'red' && <span className="ml-2 font-semibold text-red-600">Low margin</span>}
            </span>
            <span className="text-sm font-extrabold whitespace-nowrap" style={{ color: ORANGE }} data-testid="quote-line-editor-total">Line total £{formatCurrency(getLineTotal(item))}</span>
          </div>
          {lastPriceNote && (qtyUnit !== 'Each' || (!hasCf && item.code)) && <p className="text-[11px] text-gray-400 mt-1">{lastPriceNote}</p>}
        </section>
        {/* SUPPORT DOCUMENT: shown once, for the whole quote (it fills MF Support on every line whose product code is in the chosen document) */}
        <div data-testid="quote-line-editor-support-doc">
          <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Support document</Label>
          <select
            value={globalSupportDocId}
            onChange={(e) => applyGlobalSupportDocument(e.target.value)}
            className={`w-full border px-2 bg-white ${boxCls}`}
            title="Support Document: auto-fills MF Support (p) on every line whose product code is found in the chosen document"
            data-testid="global-support-document-select"
          >
            <option value="">Support Document...</option>
            {(mfSupportDocs || []).map((doc) => (
              <option key={doc.id} value={doc.id}>{doc.supplier_name} — {doc.name}</option>
            ))}
          </select>
        </div>
        {/* COSTING (everything that was in the line's drop-down) */}
        <section className="rounded-2xl border-2 p-3" style={{ borderColor: PURPLE }} data-testid="quote-line-editor-costing">
          <div className="flex items-baseline gap-2 mb-2">
            <h4 className="text-xs font-extrabold tracking-wider uppercase v3-qle-title">Costing</h4>
            {item.pricing_updated_label && <span className="text-[11px] text-gray-400">list price updated {item.pricing_updated_label}</span>}
          </div>
          <div className="grid grid-cols-3 items-end gap-2">
            <div>
              <Label className="text-[10px] text-gray-600">List price (£)</Label>
              <Input type="number" step="0.01" value={cfg?.list_price || 0} {...cost()}
                onChange={(e) => updateMarginConfig(index, 'list_price', parseFloat(e.target.value) || 0)}
                className={`${boxCls} ${calcBoxClass(cfg?.list_price)}`} data-testid="quote-line-editor-list-price" />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600" title="M2 covered per stock unit (auto-filled from the Cost Factor Master)">Cost factor (m²/unit)</Label>
              <Input type="number" step="0.001" min="0" value={item.cost_factor ?? ''} {...cost()}
                onChange={(e) => { const raw = e.target.value; updateItem(index, 'cost_factor', raw === '' ? null : (parseFloat(raw) || 0)); }}
                placeholder={priceUnit !== 'Each' ? 'N/A' : '1'}
                className={`${boxCls} ${hasCf ? 'border-green-400 bg-green-50' : (priceUnit !== 'Each' ? 'border-red-500 bg-red-50' : '')}`} data-testid="quote-line-editor-cost-factor" />
            </div>
            <div>
              <div className="flex items-center justify-between gap-1">
                <Label className="text-[10px] text-gray-600 whitespace-nowrap">CF multiplier</Label>
                <button type="button" tabIndex={-1} onClick={() => updateMarginConfig(index, 'uom_multiplier', getItemCostFactor(item))}
                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded leading-none bg-orange-100 text-orange-700 hover:bg-orange-200 whitespace-nowrap" title={`Use this product's cost factor (${getItemCostFactor(item)})`}>Use CF</button>
              </div>
              <Input type="number" step="any" min="0" value={cfg?.uom_multiplier ?? 1} {...cost()}
                onChange={(e) => { const n = parseFloat(e.target.value); if (!isNaN(n)) updateMarginConfig(index, 'uom_multiplier', n); }}
                className={`${boxCls} ${calcBoxClass(cfg?.uom_multiplier ?? 1)}`} data-testid="quote-line-editor-cf-multiplier" />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600">Discount (%)</Label>
              <Input type="number" step="0.1" value={cfg?.discount_pct || 0} {...cost()}
                onChange={(e) => updateMarginConfig(index, 'discount_pct', parseFloat(e.target.value) || 0)}
                className={`${boxCls} ${calcBoxClass(cfg?.discount_pct)}`} data-testid="quote-line-editor-discount" />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600 block truncate" title={item.ho_support_matched ? `Matched ${item.ho_support_supplier_code} / ${item.ho_support_group} in the H/O Support matrix` : 'No H/O Support matrix match for this code'}>
                H/O support (%){item.ho_support_matched && <span className="text-gray-400"> · {item.ho_support_group}</span>}
              </Label>
              <div className="relative min-w-0">
                <Input type="number" step="0.1" value={cfg?.ho_support_pct ?? ''} {...cost({ disabled: !!(item.ho_support_matched && !cfg?.ho_support_applied) })}
                  onChange={(e) => updateMarginConfig(index, 'ho_support_pct', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                  placeholder={item.ho_support_matched ? '' : '—'}
                  className={`min-w-0 pr-16 ${boxCls} ${cfg?.ho_support_applied ? calcBoxClass(cfg?.ho_support_pct) : (item.ho_support_matched ? 'border-yellow-400 bg-yellow-50' : '')}`} data-testid="quote-line-editor-ho-support" />
                <button type="button" tabIndex={-1} onClick={() => toggleHoSupportApply(index)}
                  className={`absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-semibold px-2 h-7 rounded-lg leading-none whitespace-nowrap ${cfg?.ho_support_applied ? 'bg-green-600 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                  title={cfg?.ho_support_applied ? 'Click to undo (restores Ad-hoc)' : 'Apply this H/O support % into Ad-hoc as a minus'} data-testid="quote-line-editor-ho-apply">
                  {cfg?.ho_support_applied ? 'Applied' : 'Apply'}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-gray-600">Rebate (%)</Label>
              <Input type="number" step="0.1" value={cfg?.rebate_pct || 0} {...cost()}
                onChange={(e) => updateMarginConfig(index, 'rebate_pct', parseFloat(e.target.value) || 0)}
                className={`${boxCls} ${calcBoxClass(cfg?.rebate_pct)}`} data-testid="quote-line-editor-rebate" />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600 block truncate" title="Manufacturer price support: pence per unit, taken off after Rebate">
                MF support (p){item.mf_support_document_name && <span className="text-gray-400"> · {item.mf_support_document_name}</span>}
              </Label>
              <Input type="number" step="0.1" value={((cfg?.mf_support_value || 0) * 100) || ''} {...cost()}
                onChange={(e) => updateMarginConfig(index, 'mf_support_value', (parseFloat(e.target.value) || 0) / 100)}
                placeholder="0.0" className={`min-w-0 ${boxCls} ${calcBoxClass(cfg?.mf_support_value)}`} data-testid="quote-line-editor-mf-support" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-gray-600">Ad-hoc (£)</Label>
                <div className="flex gap-0.5">
                  <button type="button" tabIndex={-1} onClick={() => updateMarginConfig(index, 'adhoc_adjustment', Math.abs(cfg?.adhoc_adjustment || 0))}
                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded leading-none ${(cfg?.adhoc_adjustment || 0) >= 0 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`} title="Add">+</button>
                  <button type="button" tabIndex={-1} onClick={() => updateMarginConfig(index, 'adhoc_adjustment', -Math.abs(cfg?.adhoc_adjustment || 0))}
                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded leading-none ${(cfg?.adhoc_adjustment || 0) < 0 ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`} title="Subtract">-</button>
                </div>
              </div>
              <Input type="number" step="0.01" value={Math.abs(cfg?.adhoc_adjustment || 0)} {...cost()}
                onChange={(e) => { const v = parseFloat(e.target.value) || 0; updateMarginConfig(index, 'adhoc_adjustment', (cfg?.adhoc_adjustment || 0) < 0 ? -v : v); }}
                placeholder="0.00" className={`${boxCls} ${calcBoxClass(cfg?.adhoc_adjustment)}`} data-testid="quote-line-editor-adhoc" />
            </div>
            <div>
              <Label className="text-[10px] text-gray-600">Margin (%)</Label>
              <Input type="number" step="0.1" value={cfg?.margin_pct || 0} {...cost()}
                onChange={(e) => updateMarginConfig(index, 'margin_pct', parseFloat(e.target.value) || 0)}
                className={`${boxCls} ${calcBoxClass(cfg?.margin_pct, { isMargin: true })}`} data-testid="quote-line-editor-margin" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500" data-testid="quote-line-editor-cost-summary">
            <span>{costBasis > 0 ? `Cost £${formatCurrency(costBasis)}` : 'No cost data for this product yet'}</span>
            {marginGbp !== null && (
              <span className={`font-semibold ${marginGbp < 0 ? 'text-red-600' : 'text-gray-600'}`}>Margin £{formatCurrency(marginGbp)} ({marginPct !== null ? marginPct.toFixed(1) : '0.0'}%)</span>
            )}
          </div>
        </section>
      </div>
      {/* Footer: next line */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-orange-100 flex-shrink-0">
        <Button type="button" variant="outline" onClick={onAddLine} className="rounded-xl h-10 px-4 text-sm font-semibold border-2 flex-1" style={{ borderColor: ORANGE, color: ORANGE }} data-testid="quote-line-editor-add-line">
          <Plus className="w-4 h-4 mr-1" /> Add new line <span className="ml-2 text-[11px] opacity-70">( + key )</span>
        </Button>
      </div>
    </div>
  );
}
